import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import PageTitle from '../components/PageTitle';

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [isFirstTime, setIsFirstTime] = useState(false);

  useEffect(() => {
    // Get session token from location state (first-time login)
    // Or check if user is authenticated (changing password from profile)
    if (location.state) {
      setSessionToken(location.state.sessionToken);
      setIsFirstTime(location.state.isFirstTime || false);
    } else {
      // If no state, user might be changing password from profile
      // In that case, we'd need to get session token differently
      // For now, redirect to login if no state
      navigate('/login');
    }
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    // Validation
    if (!formData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'New password must be at least 6 characters';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // For first-time login, current password is not required
    // For regular password change, current password is required
    if (!isFirstTime && !formData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(Object.values(errors)[0]);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    try {
      const response = await api.post('/api/auth/change-password', {
        sessionToken: sessionToken,
        currentPassword: formData.currentPassword || undefined,
        newPassword: formData.newPassword
      });

      if (response.data.message) {
        setError('');
        // Password changed successfully
        if (isFirstTime) {
          // After first-time password change, user needs to login again
          // Preserve the redirect destination (validated in Login.js)
          const redirectPath = location.state?.from || '/';
          // Validate redirect path to prevent open redirect attacks
          const isValidPath = redirectPath && 
            typeof redirectPath === 'string' &&
            redirectPath.startsWith('/') &&
            !/^https?:\/\//i.test(redirectPath) &&
            !redirectPath.startsWith('//') &&
            !/^(javascript|data):/i.test(redirectPath) &&
            !redirectPath.includes('../');
          
          alert('Password changed successfully! Please login with your new password.');
          navigate('/login', { state: { from: isValidPath ? redirectPath : '/' } });
        } else {
          // Regular password change - redirect to profile or dashboard
          alert('Password changed successfully!');
          navigate('/profile');
        }
      }
    } catch (err) {
      console.error('Change password error:', err);
      setError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="page page-center">
      <PageTitle title={isFirstTime ? "Set Your Password" : "Change Password"} />
      <div className="container container-tight py-4">
        <div className="card card-md">
          <div className="card-body">
            <div className="text-center mb-4">
              <h1 className="mb-2">Password Change Required</h1>
              {isFirstTime ? (
                <p className="text-muted">
                  This is your first time logging in. Please set a new password to continue.
                </p>
              ) : (
                <p className="text-muted">
                  Please enter your current password and choose a new password.
                </p>
              )}
            </div>
            {error && (
              <div key={error} className="alert alert-danger login-alert" role="alert">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} autoComplete="off">
              {!isFirstTime && (
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <div className="input-group input-group-flat">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      name="currentPassword"
                      className={`form-control ${fieldErrors.currentPassword ? 'is-invalid' : ''}`}
                      placeholder="Enter your current password"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      autoComplete="off"
                      required={!isFirstTime}
                    />
                    <span className="input-group-text" style={{ padding: 0 }}>
                      <a
                        href="#"
                        className="link-secondary d-flex align-items-center justify-content-center"
                        style={{ padding: '0.5rem 0.75rem', cursor: 'pointer' }}
                        title={showCurrentPassword ? 'Hide password' : 'Show password'}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowCurrentPassword(!showCurrentPassword);
                        }}
                      >
                        {showCurrentPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                            <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                            <path d="M3 3l18 18" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                          </svg>
                        )}
                      </a>
                    </span>
                  </div>
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">New Password</label>
                <div className="input-group input-group-flat">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    className={`form-control ${fieldErrors.newPassword ? 'is-invalid' : ''}`}
                    placeholder="Enter your new password"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    autoComplete="off"
                    required
                    minLength={6}
                  />
                  <span className="input-group-text" style={{ padding: 0 }}>
                    <a
                      href="#"
                      className="link-secondary d-flex align-items-center justify-content-center"
                      style={{ padding: '0.5rem 0.75rem', cursor: 'pointer' }}
                      title={showNewPassword ? 'Hide password' : 'Show password'}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowNewPassword(!showNewPassword);
                      }}
                    >
                      {showNewPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                          <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                          <path d="M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                          <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                        </svg>
                      )}
                    </a>
                  </span>
                </div>
                <small className="form-hint">
                  Password must be at least 6 characters long.
                </small>
              </div>
              <div className="mb-3">
                <label className="form-label">Confirm New Password</label>
                <div className="input-group input-group-flat">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    className={`form-control ${fieldErrors.confirmPassword ? 'is-invalid' : ''}`}
                    placeholder="Confirm your new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="off"
                    required
                    minLength={6}
                  />
                  <span className="input-group-text" style={{ padding: 0 }}>
                    <a
                      href="#"
                      className="link-secondary d-flex align-items-center justify-content-center"
                      style={{ padding: '0.5rem 0.75rem', cursor: 'pointer' }}
                      title={showConfirmPassword ? 'Hide password' : 'Show password'}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                    >
                      {showConfirmPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                          <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                          <path d="M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                          <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                        </svg>
                      )}
                    </a>
                  </span>
                </div>
              </div>
              <div className="form-footer">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? 'Changing Password...' : (isFirstTime ? 'Set Password' : 'Change Password')}
                </button>
              </div>
            </form>
            {!isFirstTime && (
              <div className="text-center text-muted mt-3">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => navigate('/profile')}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
